<template>
  <v-dialog v-model="show" width="480px" @click:outside="close">
    <v-card flat>
      <v-card-title class="d-flex align-center justify-space-between" style="padding: 16px 20px 12px;">
        <span style="font-size: 15px; font-weight: 600; color: #061D47;">User Details</span>
        <v-btn icon small @click="close" :disabled="saving">
          <v-icon size="18">close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text style="padding: 20px;">
        <div class="d-flex align-center mb-4">
          <user-avatar :item="avatarItem" size="56"></user-avatar>
          <div class="ml-3">
            <div style="font-size: 15px; font-weight: 600; color: #1a1a2e;">{{ user.name }}</div>
            <div style="font-size: 12px; color: #717e91; text-transform: capitalize;">{{ user.user_role }}</div>
          </div>
        </div>

        <div class="info-grid mb-4">
          <div class="info-row">
            <span class="info-label">Status</span>
            <span class="info-value" :style="{ color: statusColor(user.status) }">{{ user.status }}</span>
          </div>
          <div class="info-row" v-if="user.created_at">
            <span class="info-label">Joined</span>
            <span class="info-value">{{ formatDate(user.created_at) }}</span>
          </div>
        </div>

        <v-divider class="mb-4"></v-divider>

        <div class="d-flex gap-3 mb-3">
          <div style="flex: 1;">
            <div class="field-label">First Name</div>
            <div v-if="!editMode" class="field-display">{{ user.first_name || '—' }}</div>
            <v-text-field
                v-else
                v-model="editFirstName"
                dense
                outlined
                hide-details="auto"
                placeholder="First name"
                class="field-input"
                :error="!!nameError"
            ></v-text-field>
          </div>
          <div style="flex: 1;">
            <div class="field-label">Last Name</div>
            <div v-if="!editMode" class="field-display">{{ user.last_name || '—' }}</div>
            <v-text-field
                v-else
                v-model="editLastName"
                dense
                outlined
                hide-details="auto"
                placeholder="Last name"
                class="field-input"
                :error="!!nameError"
            ></v-text-field>
          </div>
        </div>
        <div v-if="editMode && nameError" style="font-size: 12px; color: #ff5252; margin-top: -8px; margin-bottom: 12px;">{{ nameError }}</div>

        <div class="mb-3" v-if="showCompany">
          <div class="field-label">Company</div>
          <div v-if="!editMode" class="field-display">{{ user.company_name || '—' }}</div>
          <v-text-field
              v-else
              v-model="editCompanyName"
              dense
              outlined
              hide-details="auto"
              placeholder="Company name"
              class="field-input"
              :error="!!companyError"
              :error-messages="companyError"
          ></v-text-field>
        </div>

        <div class="mb-3" v-if="showService">
          <div class="field-label">Service</div>
          <div v-if="!editMode" class="field-display">{{ user.service || '—' }}</div>
          <v-select
              v-else
              v-model="editService"
              :items="services"
              dense
              outlined
              hide-details="auto"
              placeholder="Select service"
              class="field-input"
              :error="!!serviceError"
              :error-messages="serviceError"
          ></v-select>
        </div>

        <div class="mb-3" v-if="showIsAdmin">
          <div class="field-label">Admin</div>
          <div v-if="!editMode" class="field-display">{{ user.is_admin ? 'Yes' : 'No' }}</div>
          <v-checkbox
              v-else
              v-model="editIsAdmin"
              label="Admin access"
              dense
              hide-details
              class="mt-1"
              color="#061D47"
          ></v-checkbox>
        </div>

        <div class="mb-3">
          <div class="field-label">Email</div>
          <div v-if="!editMode" class="field-display">{{ user.email || '—' }}</div>
          <v-text-field
              v-else
              v-model="editEmail"
              dense
              outlined
              hide-details
              placeholder="Email address"
              class="field-input"
          ></v-text-field>
        </div>

        <div>
          <div class="field-label">Phone</div>
          <div v-if="!editMode" class="field-display">{{ formatPhone(user.phone) }}</div>
          <v-text-field
              v-else
              v-model="editPhone"
              v-mask="mask"
              dense
              outlined
              hide-details
              placeholder="Phone number"
              class="field-input"
          ></v-text-field>
        </div>

        <div class="mt-3" v-if="showAddress">
          <div class="field-label">Address</div>
          <div v-if="!editMode" class="field-display">{{ (user.location && user.location.address) || '—' }}</div>
          <template v-else>
            <GmapAutocomplete
                ref="addressAutocomplete"
                placeholder="Start typing a street address…"
                :options="{ types: ['address'], componentRestrictions: { country: ['us', 'ca'] } }"
                @place_changed="onAddressChanged"
                class="address-autocomplete"
                :class="{ 'address-autocomplete--error': !!addressError }"
            ></GmapAutocomplete>
            <div v-if="addressError" style="font-size: 12px; color: #ff5252; margin-top: 4px;">{{ addressError }}</div>
            <div v-else-if="editLocationName" style="font-size: 12px; color: #19A99D; margin-top: 4px; display: flex; align-items: center; gap: 4px;">
              <v-icon size="14" color="#19A99D">place</v-icon>{{ editLocationName }}
            </div>
          </template>
        </div>

        <v-alert v-if="error" type="error" dense text dismissible class="mt-3 mb-0" @input="error = false" style="font-size: 13px;">
          {{ message_error }}
        </v-alert>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions style="padding: 12px 20px;">
        <v-spacer></v-spacer>
        <template v-if="!editMode">
          <v-btn text @click="close" style="color: #717e91; font-size: 13px;">Close</v-btn>
          <v-btn depressed @click="enableEdit" style="background-color: #061D47; color: #fff; font-size: 13px;">Edit</v-btn>
        </template>
        <template v-else>
          <v-btn text @click="cancelEdit" :disabled="saving" style="color: #717e91; font-size: 13px;">Cancel</v-btn>
          <v-btn depressed :loading="saving" @click="save" style="background-color: #061D47; color: #fff; font-size: 13px;">Save</v-btn>
        </template>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
  import {mask} from 'vue-the-mask';
  import UserAvatar from '@/components/UserAvatar/index.vue';
  import {mapState} from 'vuex';

  export default {
    name: 'EditUserModal',
    directives: {mask},
    components: {'user-avatar': UserAvatar},
    props: {
      show: {type: Boolean, default: false},
      user: {type: Object, default: () => ({})},
      hide: {type: Function, required: true},
    },
    data() {
      return {
        editMode: false,
        saving: false,
        error: false,
        message_error: null,
        nameError: null,
        companyError: null,
        serviceError: null,
        editFirstName: '',
        editLastName: '',
        editCompanyName: '',
        editService: '',
        editIsAdmin: false,
        editEmail: '',
        editPhone: '',
        editLocation: null,
        editLocationName: '',
        addressError: null,
        mask: '### - ### - ####',
      };
    },
    computed: {
      ...mapState('Users_list', ['services']),
      avatarItem() {
        const nameParts = (this.user.name || '').split(' ');
        return {
          profile_picture: this.user.profile_picture || null,
          first_name: this.user.first_name || nameParts[0] || '',
          last_name: this.user.last_name || nameParts.slice(1).join(' ') || '',
          user_role: this.user.user_role,
        };
      },
      showCompany() {
        return ['vendor', 'agent', 'client'].includes(this.user.user_role);
      },
      showService() {
        return this.user.user_role === 'vendor';
      },
      showIsAdmin() {
        return ['agent', 'employee'].includes(this.user.user_role);
      },
      showAddress() {
        return this.user.user_role === 'client';
      },
    },
    watch: {
      show(val) {
        if (val) {
          this.editMode = false;
          this.error = false;
          this.nameError = null;
          this.companyError = null;
          this.serviceError = null;
          this.addressError = null;
          if (this.showService && this.services.length === 0) {
            this.$store.dispatch('Users_list/get_services');
          }
        }
      },
    },
    methods: {
      close() {
        if (this.saving) return;
        this.editMode = false;
        this.error = false;
        this.hide();
      },
      enableEdit() {
        this.editFirstName = this.user.first_name || '';
        this.editLastName = this.user.last_name || '';
        this.editCompanyName = this.user.company_name || '';
        this.editService = this.user.service || '';
        this.editIsAdmin = this.user.is_admin || false;
        this.editEmail = this.user.email || '';
        this.editPhone = this.user.phone || '';
        this.editLocation = this.user.location || null;
        this.editLocationName = (this.user.location && this.user.location.address) || '';
        this.error = false;
        this.nameError = null;
        this.companyError = null;
        this.serviceError = null;
        this.addressError = null;
        this.editMode = true;
        // Pre-fill the autocomplete input with the address on file so the agent
        // sees the current value; an untouched field keeps the existing location.
        if (this.showAddress) {
          this.$nextTick(() => {
            const ac = this.$refs.addressAutocomplete;
            if (ac && ac.$refs && ac.$refs.input) {
              ac.$refs.input.value = this.editLocationName;
            }
          });
        }
      },
      cancelEdit() {
        this.editMode = false;
        this.error = false;
        this.nameError = null;
        this.companyError = null;
        this.serviceError = null;
        this.addressError = null;
      },
      // Mirrors the referral signup parser: postal_code is the single source of
      // truth that a complete street address was picked (not a city/country pick).
      onAddressChanged(place) {
        if (!place || !place.geometry) {
          this.editLocation = null;
          this.editLocationName = '';
          this.addressError = 'Please pick an address from the dropdown.';
          return;
        }
        const components = {};
        if (place.address_components) {
          for (const comp of place.address_components) {
            for (const t of comp.types) {
              components[t] = {short: comp.short_name, long: comp.long_name};
            }
          }
        }
        if (!components.postal_code) {
          this.editLocation = null;
          this.editLocationName = '';
          this.addressError = 'Please pick a complete street address from the dropdown (with a postal/zip code).';
          return;
        }
        this.addressError = null;
        this.editLocation = {
          address: place.formatted_address || place.name,
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
          street_number: components.street_number ? components.street_number.long : '',
          postal_code: components.postal_code.long,
          city:
            (components.locality && components.locality.long) ||
            (components.sublocality_level_1 && components.sublocality_level_1.long) ||
            '',
          state:
            (components.administrative_area_level_1 && components.administrative_area_level_1.short) || '',
          country: (components.country && components.country.short) || '',
        };
        this.editLocationName = place.formatted_address || place.name;
      },
      statusColor(status) {
        return status === 'Active' ? '#12d120' : '#717e91';
      },
      formatPhone(phone) {
        if (!phone) return '—';
        const digits = phone.replace(/\D/g, '');
        if (digits.length === 10) {
          return `${digits.slice(0, 3)} - ${digits.slice(3, 6)} - ${digits.slice(6)}`;
        }
        return phone;
      },
      formatDate(timestamp) {
        if (!timestamp) return '—';
        const d = new Date(timestamp);
        const m = d.getMonth() + 1;
        const day = String(d.getDate()).padStart(2, '0');
        const y = String(d.getFullYear()).slice(-2);
        return `${m}/${day}/${y}`;
      },
      validate() {
        this.nameError = null;
        this.companyError = null;
        this.serviceError = null;
        let valid = true;

        if (!this.editFirstName.trim()) {
          this.nameError = 'First name is required.';
          valid = false;
        } else if (!this.editLastName.trim()) {
          this.nameError = 'Last name is required.';
          valid = false;
        }

        if (this.user.user_role === 'vendor' && !this.editCompanyName.trim()) {
          this.companyError = 'Company name is required.';
          valid = false;
        }

        if (this.showService && !this.editService) {
          this.serviceError = 'Service is required.';
          valid = false;
        }

        return valid;
      },
      async save() {
        if (!this.validate()) return;

        const newEmail = this.editEmail ? this.editEmail.trim() : '';
        const newPhone = this.editPhone ? this.editPhone.replaceAll(' ', '').replaceAll('-', '') : '';

        if (newEmail !== (this.user.email || '')) {
          if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)) {
            this.error = true;
            this.message_error = 'Please enter a valid email address.';
            return;
          }
        }

        const firstNameChanged = this.editFirstName.trim() !== (this.user.first_name || '');
        const lastNameChanged = this.editLastName.trim() !== (this.user.last_name || '');
        const companyChanged = this.editCompanyName.trim() !== (this.user.company_name || '');
        const serviceChanged = this.editService !== (this.user.service || '');
        const isAdminChanged = this.showIsAdmin && this.editIsAdmin !== (this.user.is_admin || false);
        const emailChanged = newEmail !== (this.user.email || '');
        const phoneChanged = newPhone !== (this.user.phone || '');
        const oldAddress = (this.user.location && this.user.location.address) || '';
        const newAddress = (this.editLocation && this.editLocation.address) || '';
        const locationChanged = this.showAddress && newAddress !== oldAddress;

        if (!firstNameChanged && !lastNameChanged && !companyChanged && !serviceChanged && !isAdminChanged && !emailChanged && !phoneChanged && !locationChanged) {
          this.editMode = false;
          return;
        }

        this.saving = true;
        this.error = false;

        if (firstNameChanged || lastNameChanged || companyChanged || emailChanged || phoneChanged || locationChanged) {
          try {
            const userData = {owner_id: this.user.owner_id};
            if (firstNameChanged) userData.first_name = this.editFirstName.trim();
            if (lastNameChanged) userData.last_name = this.editLastName.trim();
            if (this.showCompany && companyChanged) userData.company_name = this.editCompanyName.trim();
            if (emailChanged) userData.email = newEmail;
            if (phoneChanged) userData.phone = newPhone;
            if (locationChanged) userData.location = this.editLocation;
            await this.$store.dispatch('Users_list/update_user', userData);
          } catch {
            this.saving = false;
            this.error = true;
            this.message_error = 'Failed to save changes. Please try again.';
            return;
          }
        }

        if (serviceChanged) {
          try {
            await this.$store.dispatch('Users_list/switch_service', {
              id: this.user.id,
              value: this.editService,
            });
            this.$store.commit('Users_list/updateUser', {id: this.user.id, service: this.editService});
          } catch {
            this.saving = false;
            this.error = true;
            this.message_error = 'Failed to update service. Please try again.';
            return;
          }
        }

        if (isAdminChanged) {
          await this.$store.dispatch('Users_list/switch_admin', this.user);
        }

        this.saving = false;
        this.editMode = false;
      },
    },
  };
</script>

<style>
  .v-dialog__content {
    z-index: 9999 !important;
  }
  .v-overlay--active {
    z-index: 9998 !important;
    margin-top: 0 !important;
    margin-bottom: 0 !important;
  }
</style>

<style scoped>
  .info-grid {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .info-row {
    display: flex;
    align-items: baseline;
    gap: 8px;
  }

  .info-label {
    font-size: 12px;
    color: #717e91;
    min-width: 68px;
    flex-shrink: 0;
  }

  .info-value {
    font-size: 13px;
    color: #1a1a2e;
    font-weight: 500;
  }

  .field-label {
    font-size: 12px;
    color: #717e91;
    margin-bottom: 4px;
  }

  .field-display {
    font-size: 14px;
    color: #1a1a2e;
    padding: 4px 0;
  }

  .field-input {
    font-size: 13px;
  }

  .gap-3 {
    gap: 12px;
  }

  /* Google Places autocomplete renders a bare <input> — style it to match the
     Vuetify outlined fields used elsewhere in this modal. */
  .address-autocomplete {
    width: 100%;
    height: 40px;
    border: 1px solid rgba(0, 0, 0, 0.38);
    border-radius: 4px;
    padding: 0 12px;
    font-size: 13px;
    color: #1a1a2e;
    outline: none;
    box-sizing: border-box;
  }

  .address-autocomplete:focus {
    border-color: #061D47;
    border-width: 2px;
  }

  .address-autocomplete--error {
    border-color: #ff5252;
  }
</style>
